import React, { useState, useEffect } from "react";
import { Box, Heading, Button, Layer, Text } from "grommet";
import { Close } from "grommet-icons";
import image1 from "../../assets/assessment/home.png";
import image2 from "../../assets/assessment/details.png";
import image22 from "../../assets/assessment/details2.png";
import image3 from "../../assets/assessment/edit.png";
import image33 from "../../assets/assessment/edit2.png";
import image4 from "../../assets/assessment/user.png";
import image44 from "../../assets/assessment/user2.png";
import image_create1 from "../../assets/assessment/create.png";
import image_create2 from "../../assets/assessment/create2.png";
import image_create3 from "../../assets/assessment/create3.png";
import image_create4 from "../../assets/assessment/create4.png";
import image_create5 from "../../assets/assessment/create5.png";
import image_create6 from "../../assets/assessment/create6.png";
import image_create7 from "../../assets/assessment/create7.png";

const slides = [
  {
    text: "On the Manage Assessments page, you can create new assessments, access archived assessments whose due dates have passed, search by assessment name or skill, and sort results by creation or due date. Each active assessment displays its title, due date, number of users selected and accepted, and the associated skill. Note: an assessment can have zero selected users but always includes one skill.",
    image: image1,
  },
  {
    text: "Click the Create Assessment button to initiate a new assessment. You'll be prompted to provide the assessment name, select a skill, set the due date and time, and then add participants. This is the first step in launching a new evaluation.",
    image: image_create1,
  },
  {
    text: "In the Create Assessment modal, fill in the assessment name, due date, and due time. Once all fields are completed, click on the Create Assessment button to proceed.",
    image: image_create2,
  },
  {
    text: "On the next step, optionally add users to the assessment and click Submit Selection. You can continue without adding users, but users must be added later for the assessment to be attempted.",
    image: image_create3,
  },
  {
    text: "Next, select at least one skill to be assessed and click Continue. Selecting a skill is mandatory, and it determines the question set for the assessment.",
    image: image_create4,
  },
  {
    text: "In the next step, choose the number of questions for each selected skill. You can select between 5 (minimum) and 10 (maximum) questions, then click Continue to proceed.",
    image: image_create5,
  },
  {
    text: "Review the entered details and click Proceed.",
    image: image_create6,
  },
  {
    text: "To confirm and complete the assessment creation process, click Create Assessment on the confirmation screen.",
    image: image_create7,
  },
  {
    text: "Clicking the Assessment Details button opens a detailed view in a pop-up. This includes assigned users, selected skills, and course recommendations—visible only when at least one user has completed the assessment. You can check each user's acceptance status and progress (e.g., Pending, Completed). If a user has accepted but not yet started or haven't accepted the assessment, you may send an invite. Course recommendations lead to a separate Skill Summary Report for deeper insights. Note: This section becomes accessible only after user interaction with the invitation.",
    image: image2,
  },
  {
    text: "Inside the Assessment Details pop-up, under the Users tab, you can view each user's name, whether they’ve accepted the assessment, and their current assessment status. Use the Invite Again button to resend invitations to users with pending status. This helps ensure timely engagement.",
    image: image22,
  },
  {
    text: "Use the Edit Assessment button to update the due date and time of an existing assessment. This is helpful for extending deadlines or aligning with updated schedules.",
    image: image3,
  },
  {
    text: "Inside the Edit Assessment modal, you can modify the due date and due time of the assessment. Once updated, click the Finish editing button to apply the changes. These updates help keep assessments aligned with team timelines.",
    image: image33,
  },
  {
    text: "The User Access option allows you to manage assessment participants—add or remove users, or send reminder emails to those who haven't responded. This helps ensure timely participation and effective tracking.",
    image: image4,
  },
  {
    text: "Under the User Access section, use the mail icon to send reminder emails, the delete icon to remove pending users, and the view icon to check individual user details. Note: users with active status cannot be deleted directly.",
    image: image44,
  },
];

const AssessmentsTourModal = ({ showModal, setShowModal }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (showModal) {
      setCurrentSlide(0);
    }
  }, [showModal]);

  return (
    showModal && (
      <Layer
        onEsc={() => setShowModal(false)}
        onClickOutside={() => setShowModal(false)}
        responsive={false}
      >
        <Box
          width="80vw" // Set width to 80% of the viewport width
          height="auto"
          pad={{ top: "medium", horizontal: "xsmall" }}
          round="small"
          overflow="auto"
          alignSelf="center"
        >
          <Box
            direction="row"
            align="center"
            margin={{ bottom: "small" }}
            justify="between"
          >
            {/* Step Indicator on the left */}
            <Text size="medium" margin={{ left: "small" }}>
              {`Step ${currentSlide + 1} of ${slides.length}`}
            </Text>

            {/* Title in the center */}
            <Heading
              level={2}
              margin={{ vertical: "none", right: "none", left: "105px" }}
              alignSelf="center"
              textAlign="center" // Center the text
              style={{ flexGrow: 1, display: "flex", justifyContent: "center" }} // Ensure the title is horizontally centered
            >
              Manage Assessments Overview
            </Heading>

            {/* Close Button on the right */}
            <Button
              icon={<Close />}
              margin={{ right: "small" }}
              onClick={() => setShowModal(false)}
              plain
              style={{ marginLeft: "auto" }}
            />
          </Box>

          <Box align="center" pad="small" flex="grow" width="100%">
            <img
              src={slides[currentSlide].image}
              alt="Slide Image"
              style={{
                maxWidth: "100%", // Make image take up the full width of the container
                maxHeight: "70vh", // Limit height for readability, but still larger
                objectFit: "contain", // Ensure the image maintains its aspect ratio and fits within the box
              }}
            />
            <Text textAlign="center" margin={{ top: "small" }} size="medium">
              {slides[currentSlide].text}
            </Text>
          </Box>
        </Box>
        <Box pad={{ bottom: "small", horizontal: "medium" }}>
          <Box
            direction="row"
            justify="end"
            margin={{ top: "small" }}
            width="100%"
          >
            {currentSlide > 0 && (
              <Button
                label="Previous"
                onClick={() => setCurrentSlide(currentSlide - 1)}
                secondary
              />
            )}
            {currentSlide < slides.length - 1 ? (
              <Button
                label="Next"
                onClick={() => setCurrentSlide(currentSlide + 1)}
                primary
                margin={{ left: "medium" }}
              />
            ) : (
              <Button
                label="Close"
                onClick={() => setShowModal(false)}
                primary
                margin={{ left: "medium" }}
              />
            )}
          </Box>
        </Box>
      </Layer>
    )
  );
};

export default AssessmentsTourModal;
